/**
 * Allergens as in the prototype (Beheer › Menu & prijzen › Allergenen): Tafo proposes them from the
 * category, the ingredients and keywords in the name and description; the shop confirms them.
 * The 14 allergens of EU regulation 1169/2011, with Tafo's standard list (the prototype's dictionary,
 * set right on 06/10/2026). Tafo-beheer › Allergenenlijst can replace it everywhere (GET /device/allergens).
 */
import { useSyncExternalStore } from "react";
import type { Category, Menu, Product } from "../../api/types";

export const ALLERGENS = [
  { code: "gluten", name: "Gluten", long: "Granen met gluten (tarwe, rogge, gerst, haver, spelt)" },
  { code: "schaal", name: "Schaaldieren", long: "Schaaldieren (garnalen, scampi, krab, kreeft)" },
  { code: "ei", name: "Eieren", long: "Eieren" },
  { code: "vis", name: "Vis", long: "Vis" },
  { code: "pinda", name: "Pinda's", long: "Pinda's (aardnoten)" },
  { code: "soja", name: "Soja", long: "Soja" },
  { code: "melk", name: "Melk", long: "Melk (incl. lactose)" },
  { code: "noten", name: "Noten", long: "Noten (schaalvruchten)" },
  { code: "selderij", name: "Selderij", long: "Selderij" },
  { code: "mosterd", name: "Mosterd", long: "Mosterd" },
  { code: "sesam", name: "Sesam", long: "Sesamzaad" },
  { code: "sulfiet", name: "Sulfiet", long: "Sulfiet (zwaveldioxide)" },
  { code: "lupine", name: "Lupine", long: "Lupine" },
  { code: "weekdier", name: "Weekdieren", long: "Weekdieren (mosselen, inktvis, oesters)" },
] as const;

export type AllergenCode = (typeof ALLERGENS)[number]["code"];

export function allergenName(code: string): string {
  return ALLERGENS.find((allergen) => allergen.code === code)?.name ?? code;
}

export const ALLERGEN_DISCLAIMER =
  "Tafo stelde deze allergenen automatisch voor op basis van de ingrediënten. Bij elk nieuw menu controleert Tafo ze opnieuw, maar fouten blijven mogelijk. Controleer ze en pas ze aan waar nodig. Door te bevestigen verklaart u dat de informatie juist is. Uw zaak blijft verantwoordelijk voor correcte allergeneninformatie aan uw klanten.";

/**
 * [ingredient or keyword, the allergens it contains]: Tafo's standard list. The prototype's dictionary
 * had a second column "mogelijk" for what a product might contain; Furkan (06/10/2026): "ik wil geen
 * vraagtekens zien". Each line is decided now: an allergen is in it or it is not. Decided from the
 * ingredient declarations ("bevat"; a warning for traces does not count) of the brands Belgian snack
 * bars and pizza-kebab shops buy, looked up on 06/10/2026: La William, Manna, Pauwels, D&L,
 * Vandemoortele, Bicky, Heinz, Elite and Oliehoorn for the sauces; Vanreusel, Mora, Beckers, Bicky, De
 * Vries, Ad van Geloven and Van Lieshout for the snacks; the kebab, bread, pasta, cheese and fish of the
 * wholesalers. Where the brands differ, what most of them declare counts, and with as many for as
 * against the allergen is in: a shop that confirms a list would rather strike one than miss one. The
 * dough of a pizza, pita bread and garlic bread are made with milk (Furkan, of his own shops).
 */
const DICTIONARY: readonly (readonly [string, string])[] = [
  ["Ananas", ""],
  ["Ansjovis", "vis"],
  ["Archiducsaus", "melk"],
  ["Barbecuesaus", "mosterd soja"],
  ["Bicky dressing", "ei mosterd"],
  ["Bicky hot", "gluten mosterd"],
  ["Bicky tomato", "gluten mosterd"],
  ["Bolognesesaus", "selderij"],
  ["Boulette", "gluten soja"],
  ["Broccoli", ""],
  ["Calamares", "weekdier gluten"],
  ["Champignons", ""],
  ["Cheddarkaas", "melk"],
  ["Chicken tenders", "gluten selderij"],
  ["Curryketchup", "mosterd"],
  ["Currysaus", "melk gluten mosterd selderij"],
  ["Diaboliquesaus", "melk"],
  ["Emmental", "melk"],
  ["Falafel", "gluten"],
  ["Feta", "melk"],
  ["Frieten", ""],
  ["Frietjes", ""],
  ["Frikandel", "gluten soja"],
  ["Garnalen", "schaal"],
  ["Gorgonzola", "melk"],
  ["Groenten", ""],
  ["Ham", ""],
  ["Hamburger", "gluten soja"],
  ["Jalapeños", ""],
  ["Kaas", "melk"],
  ["Kerstomaatjes", ""],
  ["Kip", ""],
  ["Kipburger", "gluten soja melk"],
  ["Kipfilet", ""],
  ["Kipsaté", "gluten soja"],
  ["Knoflook", ""],
  ["Mayonaise", "ei mosterd"],
  ["Maïs", ""],
  ["Meatballs", "gluten soja"],
  ["Mexicano", "gluten soja"],
  ["Mosselen", "weekdier"],
  ["Mozzarella", "melk"],
  ["Napolisaus", ""],
  ["Olijven", ""],
  ["Paprika", ""],
  ["Pepperoni", "melk"],
  ["Pesto", "melk noten"],
  ["Pitavlees", "gluten soja melk mosterd selderij"],
  ["Potje barbecuesaus", "mosterd soja"],
  ["Potje currysaus", "melk gluten mosterd selderij"],
  ["Potje looksaus", "ei mosterd melk"],
  ["Rode kool", ""],
  ["Rode ui", ""],
  ["Room", "melk"],
  ["Roomsaus", "melk"],
  ["Salade", ""],
  ["Salami", "melk"],
  ["Scampi", "schaal"],
  ["Shoarma", ""],
  ["Sla", ""],
  ["Spinazie", ""],
  ["Thaï curry", "schaal melk"],
  ["Tomaat", ""],
  ["Tomatensaus", ""],
  ["Tonijn", "vis"],
  ["Ui", ""],
  ["Uitjes", ""],
  ["Witte kool", ""],
  ["Wortel", ""],
  ["Zalm", "vis"],
  ["Zeevruchten", "schaal weekdier"],
  ["Zoetzuursaus", ""],
  ["Americain", "ei mosterd selderij"],
  ["Andalouse", "ei mosterd gluten"],
  ["BBQ-saus", "mosterd soja"],
  ["Brasil", "ei mosterd"],
  ["Brood", "gluten melk sesam"],
  ["Cocktail", "ei mosterd"],
  ["Curry Ketchup", "mosterd"],
  ["Gekruide kip", ""],
  ["Gele Curry", "ei mosterd"],
  ["Joppie", "ei gluten mosterd selderij"],
  ["Ketchup", ""],
  ["Looksaus", "ei mosterd melk"],
  ["Mammoet", "ei mosterd"],
  ["Penne", "gluten"],
  ["Spaghetti", "gluten"],
  ["Tagliatelle", "gluten ei"],
  ["Pepers", "sulfiet"],
  ["Pikante kruiden", ""],
  ["Pikante pepers", "sulfiet"],
  ["Pili Pili", "ei mosterd melk"],
  ["Sambal", ""],
  ["Samourai", "ei mosterd"],
  ["Samurai", "ei mosterd"],
  ["Tartaar", "ei mosterd"],
  ["Vinaigrette", "mosterd"],
  ["Zoetzuur", ""],
  ["Loempiasaus", ""],
  ["Vlees", ""],
  ["Friet", ""],
  ["Saus apart", ""],
  ["Frieten apart", ""],
  ["Weinig groenten", ""],
  ["Weinig saus", ""],
  ["Weinig vlees", ""],
  ["Pizza", "gluten melk"],
  ["Deeg", "gluten"],
  ["Fransbrood", "gluten"],
  ["Bickybroodje", "gluten sesam"],
  ["Durum", "gluten"],
  ["Lookbrood", "gluten melk"],
  ["Pasta", "gluten"],
  ["Lasagne", "gluten melk ei selderij"],
  ["Beslag", "gluten"],
  ["Paneermeel", "gluten"],
  ["Gepaneerd", "gluten"],
  ["Kroket", "gluten soja melk"],
  ["Kaaskroket", "gluten soja melk"],
  ["Bitterbal", "gluten soja melk"],
  ["Kipcorn", "gluten soja melk"],
  ["Nugget", "gluten melk soja ei"],
  ["Chix", "gluten soja melk"],
  ["Sitostick", "gluten ei soja selderij"],
  ["Loempia", "gluten soja selderij"],
  ["Mozzarella sticks", "gluten melk"],
  ["Cheddar", "melk"],
  ["Parmezaan", "melk"],
  ["Formaggi", "melk"],
  ["Carbonara", "melk ei"],
  ["Frutti di mare", "schaal weekdier"],
  ["Chicken wings", "gluten"],
  ["Potatoes", ""],
  ["Ayran", "melk"],
  ["Yoghurt", "melk"],
  ["Tzatziki", "melk"],
  ["Melk", "melk"],
  ["Boter", "melk"],
  ["Crème fraîche", "melk"],
  ["Mascarpone", "melk"],
  ["Ricotta", "melk"],
  ["Eieren", "ei"],
  ["Eigeel", "ei"],
  ["Tarwe", "gluten"],
  ["Spelt", "gluten"],
  ["Rogge", "gluten"],
  ["Gerst", "gluten"],
  ["Haver", "gluten"],
  ["Couscous", "gluten"],
  ["Bulgur", "gluten"],
  ["Sesam", "sesam"],
  ["Tahin", "sesam"],
  ["Hummus", "sesam"],
  ["Soja", "soja"],
  ["Sojasaus", "soja gluten"],
  ["Tofu", "soja"],
  ["Pinda", "pinda"],
  ["Satésaus", "pinda soja"],
  ["Saté", "gluten soja"],
  ["Noten", "noten"],
  ["Walnoot", "noten"],
  ["Walnoten", "noten"],
  ["Hazelnoot", "noten"],
  ["Hazelnoten", "noten"],
  ["Cashew", "noten"],
  ["Amandel", "noten"],
  ["Pistache", "noten"],
  ["Pecan", "noten"],
  ["Macadamia", "noten"],
  ["Mosterd", "mosterd"],
  ["Selderij", "selderij"],
  ["Lupine", "lupine"],
  ["Inktvis", "weekdier"],
  ["Octopus", "weekdier"],
  ["Calamari", "weekdier gluten"],
  ["Oester", "weekdier"],
  ["Sint-jakobsvrucht", "weekdier"],
  ["Garnaal", "schaal"],
  ["Gamba", "schaal"],
  ["Krab", "schaal"],
  ["Kreeft", "schaal"],
  ["Langoustine", "schaal"],
  ["Vis", "vis"],
  ["Kabeljauw", "vis"],
  ["Surimi", "vis ei gluten schaal"],
  ["Wijn", "sulfiet"],
  ["Kebab", "gluten soja melk mosterd selderij"],
  ["Döner", "gluten soja melk mosterd selderij"],
  ["Gyros", "mosterd soja"],
  ["Burger", "gluten sesam"],
  ["Frans broodje", "gluten"],
  ["Pizzadeeg", "gluten melk"],
  ["Pitabrood", "gluten melk sesam"],
  ["Turks brood", "gluten melk sesam"],
  ["Stokbrood", "gluten"],
  ["Pesto brood", "gluten melk noten"],
  ["Viandel", "gluten soja selderij"],
  ["Cervela", "gluten soja ei melk"],
  ["Berenpoot", "gluten soja melk"],
  ["Kipnuggets", "gluten melk soja ei"],
  ["Hot wings", "gluten"],
  ["Wedges", ""],
];

/**
 * The base of a category (dough, bread, frying), by category id of the menu: what every product of the
 * category contains, decided like the list above. The note says what else a customer should know.
 */
const CATEGORY_BASE: Record<string, { certain: string; note: string }> = {
  cat_pizzas: { certain: "gluten melk", note: "Pizzadeeg van tarwebloem, met melk." },
  cat_pastas: { certain: "gluten", note: "Pasta van tarwe. Tagliatelle bevat ook ei." },
  cat_scampi_kip: { certain: "", note: "" },
  cat_schotels: { certain: "", note: "Frieten uit de frituur: kruisbesmetting mogelijk." },
  cat_snackschotels: { certain: "", note: "Frituur: kruisbesmetting mogelijk." },
  cat_kapsalons: { certain: "", note: "Frituur: kruisbesmetting mogelijk." },
  cat_franse_broodjes: { certain: "gluten", note: "Fransbrood van tarwebloem." },
  cat_broodjes: { certain: "gluten melk sesam", note: "Pitabrood van tarwebloem, met melk en sesamzaad." },
  cat_durums: { certain: "gluten", note: "Durumbrood van tarwebloem." },
  cat_burgers: { certain: "gluten sesam", note: "Burgerbroodje van tarwebloem, met sesamzaad." },
  cat_voorgerechten: { certain: "", note: "" },
  cat_snacks: { certain: "", note: "Frituur: kruisbesmetting mogelijk." },
  cat_frieten: { certain: "", note: "Frituur: kruisbesmetting mogelijk." },
  cat_salades: { certain: "", note: "" },
  cat_dranken: { certain: "", note: "" },
  cat_extras: { certain: "", note: "" },
  cat_sauzen: { certain: "", note: "" },
};

/** The same bases by kind of category, for menus with other category ids. */
const CATEGORY_BY_NAME: readonly (readonly [RegExp, string])[] = [
  [/pizza/, "cat_pizzas"],
  [/pasta/, "cat_pastas"],
  [/frans/, "cat_franse_broodjes"],
  [/durum|wrap/, "cat_durums"],
  [/burger/, "cat_burgers"],
  [/broodje/, "cat_broodjes"],
  [/kapsalon/, "cat_kapsalons"],
  [/snackschotel/, "cat_snackschotels"],
  [/schotel/, "cat_schotels"],
  [/snack/, "cat_snacks"],
  [/friet/, "cat_frieten"],
  [/voorgerecht/, "cat_voorgerechten"],
];

export const normalize = (text: string | undefined): string =>
  (text ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[’`]/g, "'").trim();

const split = (codes: string): string[] => (codes === "" ? [] : codes.split(" "));

/** A line of the allergen list: an ingredient or keyword with its certain and possible allergens. */
export interface AllergenRule {
  key: string;
  certain: string[];
  possible: string[];
}

/**
 * Tafo's standard list, as Tafo-beheer shows and can restore it. It has no "possible" allergens; a list
 * of Tafo-beheer's own (an import of before 06/10/2026) may still carry them.
 */
export const STANDARD_ALLERGEN_RULES: readonly AllergenRule[] = DICTIONARY.map(([key, certain]) => ({
  key,
  certain: split(certain),
  possible: [],
}));

interface Entry {
  key: string;
  certain: string[];
  maybe: string[];
}

let BY_NAME = new Map<string, Entry>();
let PATTERNS: { entry: Entry; pattern: RegExp }[] = [];
let dictionaryVersion = 0;
const listeners = new Set<() => void>();

function build(rules: readonly AllergenRule[]): void {
  const byName = new Map<string, Entry>();
  const patterns: { entry: Entry; pattern: RegExp }[] = [];
  for (const rule of rules) {
    const entry = { key: rule.key, certain: [...rule.certain], maybe: [...rule.possible] };
    const normal = normalize(rule.key);
    if (normal === "") continue;
    byName.set(normal, entry);
    // a keyword without allergens is looked for too: "loempiasaus" (nothing) is not "loempia" (gluten)
    // also the singular: "garnalen" → "garnal", "pinda's" → "pinda"
    const forms = [normal];
    if (normal.endsWith("'s") && normal.length > 4) forms.push(normal.slice(0, -2));
    else if (normal.endsWith("en") && normal.length > 6) forms.push(normal.slice(0, -2));
    else if (normal.endsWith("s") && normal.length > 5) forms.push(normal.slice(0, -1));
    const escaped = forms.map((form) => form.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    patterns.push({ entry, pattern: new RegExp(`(^|[^a-z0-9])(?:${escaped.join("|")})`, "g") });
  }
  BY_NAME = byName;
  PATTERNS = patterns;
}
build(STANDARD_ALLERGEN_RULES);

/**
 * The list Tafo-beheer set (null: the standard list). Every proposal made afterwards uses it; screens
 * that show proposals re-render through `useAllergenDictionary`.
 */
export function setAllergenDictionary(rules: readonly AllergenRule[] | null): void {
  build(rules ?? STANDARD_ALLERGEN_RULES);
  dictionaryVersion++;
  for (const listener of listeners) listener();
}

/** Re-renders a component when Tafo-beheer changes the allergen list. */
export function useAllergenDictionary(): number {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => dictionaryVersion,
  );
}

export interface Suggestion {
  certain: Set<string>;
  maybe: Set<string>;
  /** Per allergen: where it comes from ("Kaas", "Categorie Pizza's", "Naam: pizza"). */
  why: Record<string, string[]>;
  /** The note of the category base, e.g. "Frituur: kruisbesmetting mogelijk." */
  note: string;
}

function categoryBase(category: Category | undefined) {
  if (!category) return undefined;
  const byId = CATEGORY_BASE[category.id];
  if (byId) return byId;
  const name = normalize(category.name);
  const match = CATEGORY_BY_NAME.find(([pattern]) => pattern.test(name));
  return match ? CATEGORY_BASE[match[1]] : undefined;
}

/**
 * Tafo's proposal for a product: category base + each ingredient + keywords in the name and the
 * description (the longest keyword wins: "loempiasaus" before "loempia"). Choice groups do not count.
 */
export function suggestAllergens(
  menu: Menu,
  product: Pick<Product, "name" | "categoryId" | "ingredients" | "description">,
): Suggestion {
  const certain = new Set<string>();
  const maybe = new Set<string>();
  const why: Record<string, string[]> = {};
  const add = (entry: { certain: string[]; maybe: string[] }, source: string) => {
    for (const code of entry.certain) {
      certain.add(code);
      (why[code] ??= []).push(source);
    }
    for (const code of entry.maybe) {
      maybe.add(code);
      (why[code] ??= []).push(`${source} (mogelijk)`);
    }
  };
  const scan = (text: string | undefined, label: (key: string) => string) => {
    const normal = normalize(text);
    if (normal === "") return;
    const hits: { entry: Entry; at: number; length: number }[] = [];
    for (const { entry, pattern } of PATTERNS) {
      for (const match of normal.matchAll(pattern)) {
        const prefix = match[1] ?? "";
        hits.push({ entry, at: match.index + prefix.length, length: match[0].length - prefix.length });
      }
    }
    for (const hit of hits) {
      // the longest keyword wins, also over a keyword inside it: "pesto brood" is not "brood" as well
      const inside = hits.some(
        (other) =>
          other !== hit &&
          other.length > hit.length &&
          other.at <= hit.at &&
          other.at + other.length >= hit.at + hit.length,
      );
      if (!inside) add(hit.entry, label(hit.entry.key));
    }
  };

  const category = menu.categories.find((candidate) => candidate.id === product.categoryId);
  const base = categoryBase(category);
  if (base) add({ certain: split(base.certain), maybe: [] }, `Categorie ${category?.name ?? ""}`);
  for (const ingredient of product.ingredients ?? []) {
    const hit = BY_NAME.get(normalize(ingredient));
    if (hit) add(hit, ingredient);
    else scan(ingredient, (key) => `${ingredient} (${key})`);
  }
  scan(product.name, (key) => `Naam: ${key}`);
  scan(product.description, (key) => `Omschrijving: ${key}`);
  for (const code of certain) maybe.delete(code);
  for (const code of Object.keys(why)) why[code] = [...new Set(why[code])];
  return { certain, maybe, why, note: base?.note ?? "" };
}

/** Every allergen of a suggestion, in the order of the list. */
export function suggestedCodes(suggestion: Suggestion): string[] {
  return ALLERGENS.map((allergen) => allergen.code).filter(
    (code) => suggestion.certain.has(code) || suggestion.maybe.has(code),
  );
}

/** "Kaas → melk\nCategorie Pizza's → gluten (mogelijk)": the reason shown when hovering an allergen. */
export function whyText(suggestion: Suggestion, code: string): string {
  return (suggestion.why[code] ?? [])
    .map((source) => {
      const possible = source.endsWith(" (mogelijk)");
      return `${source.replace(/ \(mogelijk\)$/, "")} → ${allergenName(code).toLowerCase()}${possible ? " (mogelijk)" : ""}`;
    })
    .join("\n");
}

export interface AllergenView {
  certain: Set<string>;
  maybe: Set<string>;
  /** The shop set its own list (or confirmed one); otherwise this is Tafo's proposal. */
  own: boolean;
  confirmed: { by: string; at: string } | null;
  suggestion: Suggestion;
}

/** What the shop shows: its own (confirmed) list, or else Tafo's proposal. */
export function allergenView(menu: Menu, product: Product): AllergenView {
  const suggestion = suggestAllergens(menu, product);
  const own = product.allergens !== undefined || product.allergensMaybe !== undefined;
  return {
    certain: own ? new Set(product.allergens ?? []) : suggestion.certain,
    maybe: own ? new Set(product.allergensMaybe ?? []) : suggestion.maybe,
    own,
    confirmed: product.allergensConfirmed ?? null,
    suggestion,
  };
}

export type AllergenStatus = "open" | "ok" | "diff";

/** Not confirmed, confirmed as proposed, or confirmed but different from Tafo's proposal. */
export function allergenStatus(menu: Menu, product: Product): AllergenStatus {
  const view = allergenView(menu, product);
  if (!view.confirmed) return "open";
  const mine = [...view.certain, ...view.maybe].sort().join();
  return mine === suggestedCodes(view.suggestion).sort().join() ? "ok" : "diff";
}

/**
 * The product with the given list of allergens (the toggles of the editor): an allergen the proposal
 * calls "possible" stays possible, the others are certain. The confirmation goes when the list changes.
 */
export function withAllergens(menu: Menu, product: Product, codes: readonly string[]): Product {
  const suggestion = suggestAllergens(menu, product);
  const ordered = ALLERGENS.map((allergen) => allergen.code as string).filter((code) => codes.includes(code));
  const allergens = ordered.filter((code) => !suggestion.maybe.has(code));
  const allergensMaybe = ordered.filter((code) => suggestion.maybe.has(code));
  const before = allergenView(menu, product);
  const same =
    [...before.certain].sort().join() === allergens.slice().sort().join() &&
    [...before.maybe].sort().join() === allergensMaybe.slice().sort().join();
  const { allergensConfirmed, ...rest } = product;
  return { ...rest, allergens, allergensMaybe, ...(same && allergensConfirmed ? { allergensConfirmed } : {}) };
}

/** Confirms what the shop shows now (the proposal when it set nothing itself), with who and when. */
export function confirmAllergens(menu: Menu, product: Product, by: string, at: string): Product {
  const view = allergenView(menu, product);
  return {
    ...product,
    allergens: ALLERGENS.map((allergen) => allergen.code as string).filter((code) => view.certain.has(code)),
    allergensMaybe: ALLERGENS.map((allergen) => allergen.code as string).filter((code) => view.maybe.has(code)),
    allergensConfirmed: { by, at },
  };
}
